import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { extractApiErrorMessage } from '../utils/errorHandler';
import { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('vediluxe_token'),
  loading: false,
  error: null
};

interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export const fetchCurrentUser = createAsyncThunk<User>(
  'auth/me',
  async () => {
    const response = await apiClient.get(`${API_ENDPOINTS.AUTH}/me`);
    return response.data as User;
  }
);

export const login = createAsyncThunk<
  { user: User; token: string },
  LoginPayload
>('auth/login', async (payload) => {
  const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/login`, payload);
  const { token, user } = response.data as { token: string; user: User };

  localStorage.setItem('vediluxe_token', token);

  return { user, token };
});

export interface WelcomeCoupon {
  code: string;
  percent: number;
  validUntil: string;
}

export const otpLogin = createAsyncThunk<
  { user: User; token: string; created: boolean; welcomeCoupon: WelcomeCoupon | null },
  { phone: string; otp: string; name?: string },
  { rejectValue: string }
>('auth/otpLogin', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/otp/verify`, payload);
    const { token, user, created, welcomeCoupon } = response.data as {
      token: string;
      user: User;
      created: boolean;
      welcomeCoupon: WelcomeCoupon | null;
    };

    localStorage.setItem('vediluxe_token', token);

    return { user, token, created, welcomeCoupon };
  } catch (err) {
    return rejectWithValue(extractApiErrorMessage(err, 'Incorrect OTP. Please try again.'));
  }
});

export const register = createAsyncThunk<
  { user: User; token: string },
  RegisterPayload
>('auth/register', async (payload) => {
  const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/register`, payload);
  const { token, user } = response.data as { token: string; user: User };

  localStorage.setItem('vediluxe_token', token);

  return { user, token };
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      localStorage.removeItem('vediluxe_token');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to login';
      })
      .addCase(otpLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(otpLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(otpLogin.rejected, (state) => {
        state.loading = false;
        // OTP errors are shown inline by the login page, not globally
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.loading = false;
        // Token is expired or invalid — clear it so app falls back to guest mode
        state.token = null;
        state.user = null;
        localStorage.removeItem('vediluxe_token');
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to register';
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;

